/**
 * Sign-in: two A2-style screens, email then code (plan D52). Which one shows
 * is component state, and so is the address — never the URL, never storage.
 *
 * Two places draw it (`D262`):
 *
 * - the gate, for a person without a session who took "Já tenho conta" or
 *   whose device has signed in before — with a door back to the intake,
 *   "Ainda não tenho conta";
 * - the end of the intake answered without an account, worded as what it is
 *   there — the last thing between the answers and the plan — with a way back
 *   to the answers instead.
 *
 * The code step is the same in both. What comes after the code is the
 * gate's: the session flips and it decides again.
 */
import { useCallback, useState } from "react";
import { useBackIntercept } from "../../app/back-button";
import { CodeScreen } from "./code-screen";
import {
  type CodeRequest,
  type EmailIntent,
  EmailScreen,
} from "./email-screen";

export interface AuthScreenProps {
  /** `"sign-in"` by default; `"save-plan"` at the end of the guest intake. */
  readonly intent?: EmailIntent;
  /**
   * Android Back on the e-mail step runs the door instead of leaving the app:
   * the person came here from the intake and Back returns them to it.
   */
  readonly interceptBack?: boolean;
  /** The e-mail step's quiet door: to the intake, or back to the answers. */
  readonly onNoAccount?: () => void;
}

export function AuthScreen(props: AuthScreenProps) {
  const { intent, onNoAccount } = props;
  const [email, setEmail] = useState("");
  const [request, setRequest] = useState<CodeRequest | null>(null);

  const sent = useCallback((next: CodeRequest) => {
    setEmail(next.email);
    setRequest(next);
  }, []);

  const changeEmail = useCallback(() => {
    setRequest(null);
  }, []);

  const leave = useCallback(() => onNoAccount?.(), [onNoAccount]);
  // Registered before the code step mounts, so the code step's own Back (to
  // the e-mail) still wins while it is on screen.
  useBackIntercept(
    props.interceptBack === true && onNoAccount !== undefined,
    leave
  );

  if (request === null) {
    return (
      <EmailScreen
        initialEmail={email}
        intent={intent}
        onDoor={onNoAccount}
        onSent={sent}
      />
    );
  }
  return (
    <CodeScreen onChangeEmail={changeEmail} onResent={sent} request={request} />
  );
}
